import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { RegistrationRiskLog, ReviewOutcome } from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';

import { REVIEW_OUTCOME_LABELS, TIER_LABELS } from '../constants';
import { parseMatchedSignals, signalSummary } from '../signalGrouping';
import SignalDetailsPanel from './SignalDetailsPanel';

function isBlockedOutcome(outcome: string): boolean {
  return outcome === 'CREATED_BLOCKED';
}

interface Props {
  item: RegistrationRiskLog;
  onResolve: (id: number, outcome: ReviewOutcome) => void;
}

function ReviewRow({ item, onResolve }: Props) {
  const [expanded, setExpanded] = useState(false);
  const signals = parseMatchedSignals(item.matchedSignals);
  const hasSignals = signals.length > 0;
  const summary = signalSummary(signals);
  const wasBlocked = isBlockedOutcome(item.outcome);
  const resolvedColor =
    item.reviewOutcome === 'UNBLOCKED' ? colors.success[500] : colors.gray[500];

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: colors.gray[100] },
          opacity: item.reviewed ? 0.7 : 1,
        }}
      >
        <TableCell>
          <Typography variant='body2' sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
            {item.riskScore}
          </Typography>
          <Typography variant='caption' sx={{ color: colors.gray[500] }}>
            {TIER_LABELS[item.riskTier] || item.riskTier}
          </Typography>
        </TableCell>
        <TableCell>
          {item.createdUserId ? (
            <Link
              to={`/users/lookup?id=${item.createdUserId}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography variant='body2' sx={{ color: colors.ocean[500] }}>
                #{item.createdUserId}
              </Typography>
            </Link>
          ) : (
            <Typography variant='body2' sx={{ color: colors.gray[500] }}>
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            {item.registrationEmail}
          </Typography>
          {item.normalizedEmail !== item.registrationEmail && (
            <Typography variant='caption' sx={{ color: colors.gray[500] }} display='block'>
              normalized: {item.normalizedEmail}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {hasSignals ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant='body2'>{summary}</Typography>
              <Tooltip title={expanded ? 'Hide details' : 'Show signal details'}>
                <IconButton
                  size='small'
                  aria-label={expanded ? 'Hide signal details' : 'Show signal details'}
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? (
                    <ExpandLessIcon fontSize='small' />
                  ) : (
                    <ExpandMoreIcon fontSize='small' />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Typography variant='body2' sx={{ color: colors.gray[500] }}>
              None
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant='body2'>
            {item.created ? new Date(item.created).toLocaleString() : '-'}
          </Typography>
        </TableCell>
        <TableCell>
          {item.reviewed ? (
            <Typography
              variant='body2'
              sx={{ color: resolvedColor, fontWeight: 500 }}
            >
              {REVIEW_OUTCOME_LABELS[item.reviewOutcome ?? ''] || 'Reviewed'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {wasBlocked ? (
                <>
                  <Button
                    size='small'
                    variant='outlined'
                    color='error'
                    onClick={() => onResolve(item.id, 'CONFIRMED_BLOCK')}
                  >
                    Confirm Block
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    color='success'
                    onClick={() => onResolve(item.id, 'UNBLOCKED')}
                  >
                    Unblock
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size='small'
                    variant='outlined'
                    color='error'
                    onClick={() => onResolve(item.id, 'CONFIRMED_BLOCK')}
                  >
                    Block
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    color='success'
                    onClick={() => onResolve(item.id, 'UNBLOCKED')}
                  >
                    Confirm OK
                  </Button>
                </>
              )}
            </Box>
          )}
        </TableCell>
      </TableRow>
      {hasSignals && (
        <TableRow>
          <TableCell
            colSpan={6}
            sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}
          >
            <Collapse in={expanded} timeout='auto' unmountOnExit>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: colors.gray[100],
                  borderRadius: 1,
                  my: 0.5,
                }}
              >
                <SignalDetailsPanel
                  signals={signals}
                  summaries={item.blockedUserSummaries ?? []}
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default ReviewRow;
